import React, { createContext, ReactElement, useContext, useEffect, useReducer } from 'react'
import im from 'immer'
import compareAsc from 'date-fns/compareAsc'

import {
  PRESENTATION_CONTEXT,
  PresentationContent,
  PresentationContentState,
  PresentationState,
} from './types'
import equal from 'fast-deep-equal'
import {
  ControlData,
  PlayerEventData,
  PlayerEvents,
} from '../../../index';
import usePlayerEvent from '../../../hooks/usePlayerEvent/usePlayerEvent'
import { FileType } from '@alucio/aws-beacon-amplify/src/models'

type PresentationStateContextAction = {
  type: 'presentContent' | 'startScreenShare' | 'stopPresenting' | 'updateContentState'
  | 'updateFromMessage' | 'resetMeetingLog' | 'recordControlDataMessage' | 'saveMeetingLog' | 'updateVirtualSessionId',
  payload: PresentationState | PresentationContent
  | PresentationContentState | ControlData | string | (() => void) | undefined,
}

const emptyContentState: PresentationContent = {
  JWT: '',
  bucket: '',
  docPath: '',
  documentVersionId: '',
  documentId: '',
  contentType: FileType.PDF,
  visiblePages: [],
  state: {},
  watermarkText: '',
  contentURL: '',
  isContentCached: false,
}

const initialState: PresentationState = {
  context: PRESENTATION_CONTEXT.APP,
  lastUpdated: new Date('2000-01-01'),
  content: emptyContentState,
}

type PresentationStateContextValue = PresentationState & {
  dispatch: React.Dispatch<PresentationStateContextAction>,
}

const PresentationStateContext = createContext<PresentationStateContextValue>({
  ...initialState,
  dispatch: () => { },
});

PresentationStateContext.displayName = 'PresentationStateContext'
export const usePresentationContext = () => useContext(PresentationStateContext)

const presentationStateReducer = (state: PresentationState, action: PresentationStateContextAction) => {
  switch (action.type) {
    case 'updateContentState': {
      const contentState = action.payload as PresentationContentState
      return im(state, draft => {
        if (state.content) {
          const newContentState = {
            ...state.content.state,
            ...contentState,
          }
          if (draft.content && !equal(state.content.state, newContentState)) {
            draft.content.state = newContentState
            draft.lastUpdated = new Date()
          }
        }
      })
    }
    case 'updateFromMessage': {
      const message = action.payload as PresentationState
      return im(state, draft => {
        if (compareAsc(state.lastUpdated, message.lastUpdated) === -1) {
          if (
            message.content &&
            state.content &&
            (state.content.documentVersionId !== message.content.documentVersionId ||
              state.content.customDeckId !== message.content.customDeckId ||
              state.content.groupId !== message.content.groupId)
          ) {
            // Content has switched
            draft.content = message.content
          }
          else if (
            draft.content &&
            state.content &&
            message.content &&
            !equal(state.content.state, message.content.state)
          ) {
            draft.content.state = message.content.state
          }
          draft.lastUpdated = message.lastUpdated
        }
      })
    }
    default: return state
  }
}

const handlePlayerEvent = (playerEvent: MessageEvent | undefined, dispatch) => {
  if (playerEvent && playerEvent.data) {
    const data = playerEvent.data as PlayerEventData
    if (data.event === PlayerEvents.statusEvent) {
      const payload: PresentationContentState = {
        page: data.param.slide,
        step: data.param.step,
      }
      dispatch({
        type: 'updateContentState',
        payload: payload,
      })
    }
  }
}

export interface PresentationContextProps {
  context: PRESENTATION_CONTEXT,
  children: ReactElement<any>
}

export const PresentationContext: React.FC<PresentationContextProps> = (props) => {
  const { children, context } = props
  const [presentationState, dispatch] = useReducer(presentationStateReducer, { ...initialState, context })

  // Capture events from PPTX Viewer
  const playerEvent = usePlayerEvent()
  useEffect(() => {
    handlePlayerEvent(playerEvent, dispatch)
  }, [playerEvent])

  return (
    <PresentationStateContext.Provider value={{ ...presentationState, dispatch }}>
      {children}
    </PresentationStateContext.Provider>
  )
}
